import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { useMemo } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { FiltersTypes } from "@common/types/filters/general";
import { DeepOmit } from "@common/types/base/generics";
import { Class } from "@common/types/class";
import { classContract } from "@common/contracts/class.contract";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { useScope } from "@state/scopeContext.lib";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { showErrorToast } from "@design-system/ToastMessages/useToaster";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { ClassWithChildren, getNormalizedClasses } from "@helpers/class";
import { queryClient } from "../queryClient";
import {
    getApiErrorMessage,
    getApiErrorStatus,
    retryUnlessStatus,
} from "./apiError";

const classContractClient = getBackendAPIClient(classContract);

const FORBIDDEN_STATUS = 403;
const CLASSES_LOAD_ERROR_TOAST_KEY = "classes-load-error";

const getClassesQueryKey = (workspaceId: string) => ["classes", workspaceId];

type ChangeClassPositionSchema = DeepOmit<
    ServerInferRequest<typeof classContract.changeClassPosition>,
    "params",
    "workspaceId"
>;

function getOptimisticallyReorderedClasses(
    classes: Class[],
    schema: ChangeClassPositionSchema,
): Class[] {
    const classId = schema.params.id;
    const nextParentClassId = schema.body.parentClassId;
    const classToMove = classes.find((classItem) => classItem.id === classId);

    if (!classToMove || classToMove.parentClassId !== nextParentClassId) {
        return classes;
    }

    const siblings = classes
        .filter((classItem) => classItem.parentClassId === nextParentClassId)
        .toSorted(
            (leftClass, rightClass) => leftClass.order - rightClass.order,
        );

    const sourceIndex = siblings.findIndex(
        (classItem) => classItem.id === classToMove.id,
    );
    if (sourceIndex === -1) {
        return classes;
    }

    const rawTargetIndex = schema.body.order ?? sourceIndex;
    const targetIndex = Math.max(
        0,
        Math.min(rawTargetIndex, siblings.length - 1),
    );

    if (sourceIndex === targetIndex) {
        return classes;
    }

    const reorderedSiblings = arrayMove(siblings, sourceIndex, targetIndex);
    const siblingOrderMap = new Map(
        reorderedSiblings.map((classItem, index) => [classItem.id, index]),
    );

    return classes.map((classItem) => {
        if (classItem.parentClassId !== nextParentClassId) {
            return classItem;
        }

        const updatedOrder = siblingOrderMap.get(classItem.id);
        return updatedOrder === undefined
            ? classItem
            : { ...classItem, order: updatedOrder };
    });
}

export function useClasses(): {
    classes: Class[];
    classesWithChildren: ClassWithChildren[];
    classesMap: Map<string, ClassWithChildren>;
    sortedClassesIndexMap: Map<string, number>;
    isActivated: boolean;
} {
    const { hasAccess, isFetched } = useAllowFeature(PREMIUM_FEATURES.CLASSES);
    const { workspaceId } = useScope();

    const classesQuery = useQuery({
        queryKey: workspaceId ? getClassesQueryKey(workspaceId) : [],
        queryFn: async () => {
            if (!workspaceId) {
                return;
            }
            try {
                const result = await classContractClient.getClasses({
                    extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                    params: { workspaceId },
                });
                return result.body.classes;
            } catch (error) {
                if (getApiErrorStatus(error) !== FORBIDDEN_STATUS) {
                    showErrorToast(
                        getApiErrorMessage(error, "Failed to load classes"),
                        CLASSES_LOAD_ERROR_TOAST_KEY,
                    );
                }
                throw error;
            }
        },
        retry: retryUnlessStatus([FORBIDDEN_STATUS]),
        enabled: hasAccess && isFetched,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    const normalizedClasses = useMemo(() => {
        if (!classesQuery.data) {
            return {
                sortedClasses: [],
                classesWithChildren: [],
                classesMap: new Map(),
                sortedClassesIndexMap: new Map(),
            };
        }

        return getNormalizedClasses(classesQuery.data);
    }, [classesQuery.data]);

    return {
        classes: normalizedClasses.sortedClasses,
        classesWithChildren: normalizedClasses.classesWithChildren,
        classesMap: normalizedClasses.classesMap,
        sortedClassesIndexMap: normalizedClasses.sortedClassesIndexMap,
        isActivated: hasAccess && normalizedClasses.sortedClasses.length > 0,
    };
}

export function useCreateClassMutation() {
    const { workspaceId } = useScope();
    if (!workspaceId) {
        throw new Error("Expecting active workspace");
    }
    return useMutation({
        mutationFn: async (
            schema: ServerInferRequest<
                typeof classContract.createClass
            >["body"],
        ) => {
            const result = await classContractClient.createClass({
                params: { workspaceId },
                body: schema,
            });
            return result.body.class;
        },
        onSuccess: async (createdClass) => {
            queryClient.setQueryData(getClassesQueryKey(workspaceId), [
                ...(queryClient.getQueryData<Class[]>(
                    getClassesQueryKey(workspaceId),
                ) ?? []),
                createdClass,
            ]);
            invalidateClassesQueries(workspaceId);
        },
    });
}

export function useUpdateClassMutation() {
    const { workspaceId } = useScope();
    if (!workspaceId) {
        throw new Error("Expecting active workspace");
    }
    return useMutation({
        mutationFn: async (
            schema: DeepOmit<
                ServerInferRequest<typeof classContract.updateClass>,
                "params",
                "workspaceId"
            >,
        ) => {
            const result = await classContractClient.updateClass({
                params: {
                    workspaceId,
                    id: schema.params.id,
                },
                body: schema.body,
            });
            return result.body.class;
        },
        onSuccess: async (updatedClass) => {
            if (!updatedClass) {
                return;
            }
            const previousData =
                queryClient.getQueryData<Class[]>(
                    getClassesQueryKey(workspaceId),
                ) ?? [];

            queryClient.setQueryData(
                getClassesQueryKey(workspaceId),
                previousData.map((c) =>
                    c.id === updatedClass.id ? updatedClass : c,
                ),
            );
            invalidateClassesQueries(workspaceId);
        },
    });
}

export function useChangeClassPositionMutation() {
    const { workspaceId } = useScope();
    const { isViewOnly } = useViewOnlyRefusal();
    if (!workspaceId) {
        throw new Error("Expecting active workspace");
    }
    return useMutation({
        mutationFn: async (schema: ChangeClassPositionSchema) => {
            const result = await classContractClient.changeClassPosition({
                params: {
                    workspaceId,
                    id: schema.params.id,
                },
                body: schema.body,
            });
            return result.body.class;
        },
        onMutate: async (schema) => {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            await queryClient.cancelQueries({
                queryKey: getClassesQueryKey(workspaceId),
            });
            const previousData =
                queryClient.getQueryData<Class[]>(
                    getClassesQueryKey(workspaceId),
                ) ?? [];

            queryClient.setQueryData(
                getClassesQueryKey(workspaceId),
                getOptimisticallyReorderedClasses(previousData, schema),
            );

            return { previousData };
        },
        onError: (_error, _variables, context) => {
            if (!context?.previousData) {
                return;
            }

            queryClient.setQueryData(
                getClassesQueryKey(workspaceId),
                context.previousData,
            );
        },
        onSuccess: () => {
            invalidateClassesQueries(workspaceId);
        },
    });
}

export function useDeleteClassMutation() {
    const { workspaceId } = useScope();
    if (!workspaceId) {
        throw new Error("Expecting active workspace");
    }
    return useMutation({
        mutationFn: async (id: string) => {
            await classContractClient.deleteClass({
                params: { workspaceId, id },
            });
        },
        onSuccess: async (_, id) => {
            const previousData =
                queryClient.getQueryData<Class[]>(
                    getClassesQueryKey(workspaceId),
                ) ?? [];

            queryClient.setQueryData(
                getClassesQueryKey(workspaceId),
                previousData.filter((c) => c.id !== id),
            );

            invalidateClassesQueries(workspaceId);
        },
    });
}

function invalidateClassesQueries(workspaceId: string) {
    queryClient.invalidateQueries({
        queryKey: getClassesQueryKey(workspaceId),
    });
}

export function useClassStatistics(
    filters: Pick<FiltersTypes, "entityIds" | "start" | "end">,
) {
    const { workspaceId } = useScope();
    const { hasAccess, isFetched } = useAllowFeature(PREMIUM_FEATURES.CLASSES);

    if (!workspaceId) {
        throw new Error("No active workspace");
    }

    return useQuery({
        queryKey: ["classStatistics", filters],
        queryFn: () =>
            classContractClient.getClassStatistics({
                query: {
                    entityIds: filters.entityIds,
                    start: filters.start,
                    end: filters.end,
                },
                params: { workspaceId },
            }),
        placeholderData: keepPreviousData,
        enabled: hasAccess && isFetched,
    });
}
